import React, { useCallback, useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useMedia } from "@/contexts/MediaContext";
import VideoControls from "../VideoControls";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import { BuildSteps } from "@/types/flows";
import { useFlow } from "@/contexts/FlowContext";
import { useFilePreview } from "@/contexts/FilePreviewContext";
import { useModel } from "@/contexts/ModelContext";
import { fileCarouselHintContainerId } from "../useCarousel";
import { useFrameStable } from "@/contexts/FrameContext";
import { FileControls } from "./FileControls";
import { FileSwitcher } from "./FileSwitcher";
import { FileUploadModal } from "@/components/file/UploadModal/FileUploadModal";
import { useFlowState } from "@/hooks/useFlowState";
import {
    useFileCarouselNavigation,
    useFileCarouselThumbnails,
    useFileCarouselKeyboardNavigation,
    useFileCarouselAutoScroll
} from "@/hooks/useFileCarousel";
import { FloatingNavigationControls } from "@/components/shared/FloatingNavigationControls";
import { useCurrentMedia } from "@/store";
import { UploadLimitErrorType } from "@/constants/media";
import { FileInputArea } from "@/components/flow/shared/FileInputArea";
import { UploadLimitHint } from "@/components/flow/build/Hints/UploadLimitHint";
import { useUploadAgain } from "@/hooks/useUploadAgain";
import { MediaItem } from "@/types/media";
import { VideoControls as VideoControlsType } from "@/contexts/FilePreviewContext";

// Container ID constants to avoid hardcoded strings
const THUMBNAIL_CONTAINER_ID = "thumbnail-container";

/**
 * Loading spinner shown while fetching a random sample image.
 */
const LoadingSpinner = () => (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-purple-600" />
        <span className="text-sm text-gray-500">Loading sample image...</span>
    </div>
);

/**
 * Floating hint container for upload limit errors.
 * Only renders the hint when files exist (hasNoFiles is false).
 */
const FloatingHintContainer = ({
    hasNoFiles,
    uploadLimitError,
    handleUploadAgain,
    clearUploadLimitError
}: {
    hasNoFiles: boolean;
    uploadLimitError?: UploadLimitErrorType | null;
    handleUploadAgain?: () => void;
    clearUploadLimitError?: () => void;
}) => (
    <div id={fileCarouselHintContainerId} className="floatingHint">
        {!hasNoFiles && (
            <UploadLimitHint
                uploadLimitError={uploadLimitError ?? null}
                onUploadAgain={handleUploadAgain}
                onDismiss={clearUploadLimitError}
            />
        )}
    </div>
);

interface EmptyStateContentProps {
    isLoadingRandomSource: boolean;
    addMediaItems: (files: File[]) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
}

/**
 * Content shown when no files are loaded - either a loading spinner or the file input area.
 */
const EmptyStateContent = ({
    isLoadingRandomSource,
    addMediaItems,
    uploadLimitError
}: EmptyStateContentProps) => (
    <div className="flex h-full w-full flex-col items-center gap-4 p-4">
        {isLoadingRandomSource ? (
            <LoadingSpinner />
        ) : (
            <FileInputArea
                addMediaItems={addMediaItems}
                showAllMethods={false}
                uploadLimitError={uploadLimitError}
            />
        )}
    </div>
);

export enum FileCarouselLayout {
    DEFAULT = "default",
    MODEL = "model"
}

interface FilePreviewAreaProps {
    children: React.ReactNode;
    layout: FileCarouselLayout;
    flowLocked: boolean;
    videoControls: VideoControlsType | null;
    currentMedia: ReturnType<typeof useCurrentMedia>;
    hasMultipleFiles: boolean;
    hasNoFiles: boolean;
    addMediaItems: (files: File[]) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
    handlePrevFile: () => void;
    handleNextFile: () => void;
    isHovered: boolean;
    setIsHovered: (hovered: boolean) => void;
    isLoadingRandomSource?: boolean;
    handleUploadAgain?: () => void;
}

/**
 * File preview area component containing the main content display,
 * file controls header, navigation arrows, and video controls.
 */
const FilePreviewArea = ({
    children,
    layout,
    flowLocked,
    videoControls,
    currentMedia,
    hasMultipleFiles,
    hasNoFiles,
    addMediaItems,
    uploadLimitError,
    clearUploadLimitError,
    handlePrevFile,
    handleNextFile,
    isHovered,
    setIsHovered,
    isLoadingRandomSource = false,
    handleUploadAgain
}: FilePreviewAreaProps) => {
    const isModelLayout = layout === FileCarouselLayout.MODEL;

    return (
        <div
            className={twClassNames(
                "fileCarouselWindow relative flex-1",
                !isModelLayout && "overflow-hidden",
                isModelLayout &&
                    "!rounded-none !border-0 !shadow-none [&_.filePreview]:!rounded-none",
                !isModelLayout && !videoControls && "rounded-b-xl",
                flowLocked && "cursor-not-allowed"
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <FileControls
                filename={currentMedia?.file.file?.name}
                flowLocked={flowLocked}
                layout={layout}
            />

            <div
                className={twClassNames(
                    "relative flex min-h-0 flex-1 flex-col",
                    flowLocked && "cursor-not-allowed"
                )}
            >
                <FloatingHintContainer
                    hasNoFiles={hasNoFiles}
                    uploadLimitError={uploadLimitError}
                    handleUploadAgain={handleUploadAgain}
                    clearUploadLimitError={clearUploadLimitError}
                />
                <div
                    className={twClassNames(
                        "flex min-h-0 flex-1 flex-col",
                        flowLocked && "cursor-not-allowed"
                    )}
                >
                    {hasNoFiles ? (
                        <EmptyStateContent
                            isLoadingRandomSource={isLoadingRandomSource}
                            addMediaItems={addMediaItems}
                            uploadLimitError={uploadLimitError}
                        />
                    ) : (
                        children
                    )}
                </div>
            </div>

            {hasMultipleFiles && isHovered && !flowLocked && (
                <FloatingNavigationControls
                    onPrevious={handlePrevFile}
                    onNext={handleNextFile}
                    buttonType="dark-primary"
                    horizontalControls={false}
                />
            )}

            {currentMedia?.file.isVideo && (
                <VideoControls controls={videoControls} flowLocked={flowLocked} />
            )}
        </div>
    );
};

interface FilePreviewWithSidebarProps {
    children: React.ReactNode;
    layout: FileCarouselLayout;
    flowLocked: boolean;
    videoControls: VideoControlsType | null;
    currentMedia: ReturnType<typeof useCurrentMedia>;
    hasMultipleFiles: boolean;
    hasNoFiles: boolean;
    addMediaItems: (files: File[]) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
    handlePrevFile: () => void;
    handleNextFile: () => void;
    isHovered: boolean;
    setIsHovered: (hovered: boolean) => void;
    orderedMediaItems: MediaItem[];
    thumbnails: Record<string, string>;
    deleteMediaItem: (id: string) => void;
    isTestingNewFiles: boolean;
    isReviewStep: boolean;
    isDownloading: boolean;
    isImproveMode?: boolean;
    isComparisonMode?: boolean;
    approvedImageIds?: Set<string>;
    containerId?: string;
    isLoadingRandomSource?: boolean;
    erroredMediaItems?: any[];
    handleUploadAgain?: () => void;
}

/**
 * Main content area with file preview and thumbnail sidebar.
 * In DEFAULT mode: sidebar on left, preview on right with gap.
 * In MODEL mode: FileSwitcher shows on hover over preview, FileInputArea on right side.
 */
const FilePreviewWithSidebar = ({
    children,
    layout,
    flowLocked,
    videoControls,
    currentMedia,
    hasMultipleFiles,
    hasNoFiles,
    addMediaItems,
    uploadLimitError,
    clearUploadLimitError,
    handlePrevFile,
    handleNextFile,
    isHovered,
    setIsHovered,
    orderedMediaItems,
    thumbnails,
    deleteMediaItem,
    isTestingNewFiles,
    isReviewStep,
    isDownloading,
    isImproveMode = false,
    isComparisonMode = false,
    approvedImageIds,
    containerId = THUMBNAIL_CONTAINER_ID,
    isLoadingRandomSource = false,
    erroredMediaItems = [],
    handleUploadAgain
}: FilePreviewWithSidebarProps) => {
    const isModelLayout = layout === FileCarouselLayout.MODEL;

    // In MODEL layout, FileSwitcher shows on hover with isShareMode styling (horizontal, overlay)
    const fileSwitcher = (
        <FileSwitcher
            orderedMediaItems={orderedMediaItems}
            hasMultipleFiles={hasMultipleFiles}
            thumbnails={thumbnails}
            deleteMediaItem={deleteMediaItem}
            isTestingNewFiles={isTestingNewFiles}
            isReviewStep={isReviewStep}
            onAddMediaItems={addMediaItems}
            isDownloading={isDownloading}
            containerId={containerId}
            layout={layout}
            isImproveMode={isImproveMode}
            isComparisonMode={isComparisonMode}
            approvedImageIds={approvedImageIds}
            isShareMode={isModelLayout}
        />
    );

    // For MODEL layout, render preview with hover-based FileSwitcher and FileInputArea sidebar
    if (isModelLayout) {
        return (
            <div
                className={twClassNames(
                    "flex min-h-0 flex-1 flex-row",
                    isComparisonMode ? "overflow-visible" : "overflow-hidden"
                )}
            >
                {/* File Preview Area with hover-based FileSwitcher */}
                <div
                    className={twClassNames(
                        "group relative flex flex-col",
                        // Full width in comparison mode (no sidebar), otherwise 3/4
                        isComparisonMode
                            ? "min-h-0 w-full"
                            : "fileCarouselWindow overflow-hidden md:w-3/4",
                        !isComparisonMode &&
                            "!rounded-none !border-0 !shadow-none [&_.filePreview]:!rounded-none",
                        flowLocked && "cursor-not-allowed"
                    )}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                >
                    {/* Hide FileControls header in comparison mode - each panel has its own header */}
                    {!isComparisonMode && (
                        <FileControls
                            filename={currentMedia?.file.file?.name}
                            flowLocked={flowLocked}
                            layout={layout}
                            isComparisonMode={isComparisonMode}
                        />
                    )}

                    <div
                        className={twClassNames(
                            "relative flex min-h-0 flex-1 flex-col",
                            flowLocked && "cursor-not-allowed"
                        )}
                    >
                        {/* FileSwitcher - Show on hover when multiple files */}
                        <AnimatePresence>
                            {hasMultipleFiles && isHovered && (
                                <motion.div
                                    className="absolute bottom-4 left-4 right-4 z-10 flex justify-center"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    {fileSwitcher}
                                </motion.div>
                            )}
                        </AnimatePresence>

                        {/* In MODEL layout, upload limit errors are shown in the FileInputArea sidebar */}
                        <div className="relative flex min-h-0 flex-1 flex-col">
                            {hasMultipleFiles && isHovered && (
                                <FloatingNavigationControls
                                    onPrevious={handlePrevFile}
                                    onNext={handleNextFile}
                                    buttonType="dark-primary"
                                    disabled={flowLocked}
                                    horizontalControls={true}
                                />
                            )}
                            {hasNoFiles ? (
                                <EmptyStateContent
                                    isLoadingRandomSource={isLoadingRandomSource}
                                    addMediaItems={addMediaItems}
                                    uploadLimitError={uploadLimitError}
                                />
                            ) : (
                                children
                            )}
                        </div>
                    </div>

                    {currentMedia?.file.isVideo && (
                        <VideoControls controls={videoControls} flowLocked={flowLocked} />
                    )}
                </div>

                {/* FileInputArea sidebar on the right - hidden in comparison mode */}
                {!isComparisonMode && (
                    <div className="flex w-1/4 min-w-72 flex-col gap-3 border-l border-gray-300 bg-white p-6">
                        <FileInputArea
                            addMediaItems={addMediaItems}
                            erroredMediaItems={erroredMediaItems}
                            showAllMethods={false}
                            qrSize={80}
                            uploadLimitError={uploadLimitError}
                        />
                    </div>
                )}
            </div>
        );
    }

    // DEFAULT layout: sidebar on left, preview on right with gap
    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-row gap-4">
            {fileSwitcher}

            <FilePreviewArea
                layout={layout}
                flowLocked={flowLocked}
                videoControls={videoControls}
                currentMedia={currentMedia}
                hasMultipleFiles={hasMultipleFiles}
                hasNoFiles={hasNoFiles}
                addMediaItems={addMediaItems}
                uploadLimitError={uploadLimitError}
                clearUploadLimitError={clearUploadLimitError}
                handlePrevFile={handlePrevFile}
                handleNextFile={handleNextFile}
                isHovered={isHovered}
                setIsHovered={setIsHovered}
                isLoadingRandomSource={isLoadingRandomSource}
                handleUploadAgain={handleUploadAgain}
            >
                {children}
            </FilePreviewArea>
        </div>
    );
};

interface FileCarouselProps {
    children: React.ReactNode;
    className?: string;
    onAddMediaItems: (files: File[], onFinally?: () => void) => Promise<void>;
    uploadLimitError?: UploadLimitErrorType | null;
    clearUploadLimitError?: () => void;
    /**
     * Layout mode for the carousel:
     * - DEFAULT: FileSwitcher on left, file preview on right with gap
     * - MODEL: FileSwitcher on right, no gap, no rounded corners
     */
    layout?: FileCarouselLayout;
    isImproveMode?: boolean;
    isComparisonMode?: boolean;
    isLoadingRandomSource?: boolean;
    approvedImageIds?: Set<string>;
}

export const FileCarousel: React.FC<FileCarouselProps> = ({
    children,
    className,
    onAddMediaItems,
    uploadLimitError: uploadLimitErrorProp,
    clearUploadLimitError: clearUploadLimitErrorProp,
    layout = FileCarouselLayout.DEFAULT,
    isImproveMode = false,
    isComparisonMode = false,
    isLoadingRandomSource = false,
    approvedImageIds
}) => {
    const isModelLayout = layout === FileCarouselLayout.MODEL;
    const { flowLocked } = useFlow();
    const { step } = useFlowState();
    const { hasMultipleFiles, videoControls } = useFilePreview();
    const currentMedia = useCurrentMedia();
    const {
        deleteMediaItem,
        erroredMediaItems,
        orderedMediaItems,
        uploadLimitError: uploadLimitErrorFromContext,
        clearUploadLimitError: clearUploadLimitErrorFromContext
    } = useMedia();
    const { isTestingNewFiles, isDownloading } = useModel();

    // Use props if provided (e.g., from useTestWithMoreFiles), otherwise fall back to context
    const uploadLimitError = uploadLimitErrorProp ?? uploadLimitErrorFromContext;
    const clearUploadLimitError = clearUploadLimitErrorProp ?? clearUploadLimitErrorFromContext;

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const isReviewStep = useMemo(() => step === BuildSteps.REVIEW, [step]);
    const { orderedTimelineFramePreviewUrls } = useFrameStable();

    const thumbnails = useFileCarouselThumbnails(
        orderedMediaItems,
        orderedTimelineFramePreviewUrls
    );
    const { handlePrevFile, handleNextFile } = useFileCarouselNavigation({
        mediaItems: orderedMediaItems,
        fileId: currentMedia?.file.id,
        flowLocked,
        hasMultipleFiles
    });

    useFileCarouselAutoScroll({
        fileId: currentMedia?.file.id,
        hasMultipleFiles,
        containerId: THUMBNAIL_CONTAINER_ID
    });
    useFileCarouselKeyboardNavigation(hasMultipleFiles, flowLocked, handlePrevFile, handleNextFile);

    const closeModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const addMediaItems = useCallback(
        async (files: File[]) => {
            if (flowLocked) {
                console.warn("🎠 Carousel: Flow is locked, returning");
                return;
            }

            await onAddMediaItems(files, closeModal);
        },
        [onAddMediaItems, closeModal, flowLocked]
    );

    const { fileInputRef, handleUploadAgain, handleFileSelected } = useUploadAgain({
        addMediaItems,
        clearUploadLimitError
    });

    const hasNoFiles = orderedMediaItems.length === 0;

    return (
        <div
            className={twClassNames(
                "flex h-full min-h-0 w-full",
                isModelLayout
                    ? isComparisonMode
                        ? "flex-col gap-3" // Comparison mode: no outer border, just gap between header and panels
                        : "border-border flex-col overflow-hidden rounded-xl border"
                    : "flex-row gap-4",
                className
            )}
        >
            {/* Hidden file input for "Upload Again" button */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*"
                multiple
                onChange={handleFileSelected}
                className="hidden"
            />

            <FilePreviewWithSidebar
                layout={layout}
                flowLocked={flowLocked}
                videoControls={videoControls}
                currentMedia={currentMedia}
                hasMultipleFiles={hasMultipleFiles}
                hasNoFiles={hasNoFiles}
                addMediaItems={addMediaItems}
                uploadLimitError={uploadLimitError}
                clearUploadLimitError={clearUploadLimitError}
                handlePrevFile={handlePrevFile}
                handleNextFile={handleNextFile}
                isHovered={isHovered}
                setIsHovered={setIsHovered}
                orderedMediaItems={orderedMediaItems}
                thumbnails={thumbnails}
                deleteMediaItem={deleteMediaItem}
                isTestingNewFiles={isTestingNewFiles}
                isReviewStep={isReviewStep}
                isDownloading={isDownloading}
                isImproveMode={isImproveMode}
                isComparisonMode={isComparisonMode}
                approvedImageIds={approvedImageIds}
                containerId={THUMBNAIL_CONTAINER_ID}
                isLoadingRandomSource={isLoadingRandomSource}
                erroredMediaItems={erroredMediaItems}
                handleUploadAgain={handleUploadAgain}
            >
                {children}
            </FilePreviewWithSidebar>

            {isModalOpen && (
                <FileUploadModal
                    title={isReviewStep ? "Test on More Files" : "Add Files"}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    onAddMediaItems={addMediaItems}
                    erroredMediaItems={erroredMediaItems}
                    uploadLimitError={uploadLimitError}
                />
            )}
        </div>
    );
};
